import { useState, useEffect, useRef } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { Fingerprint } from 'lucide-react';

interface Props {
  sessionId: string;
  apiBase: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

// Global variable to prevent duplicate concurrent WebAuthn calls (e.g. in React 18 StrictMode)
let activeWebAuthnSessionId: string | null = null;

/**
 * BiometryVerifier — Aciona biometria no celular via WebAuthn.
 *
 * Dois modos (decidido pelo backend):
 *  - register: Primeiro acesso — cadastra nova Passkey (usa biometria do aparelho)
 *  - verify:   Acesso posterior — verifica com Passkey existente
 *
 * Doc: docs_autentica_seguranca_hard/01-fluxo-mobile-biometria.md — Fase B
 */
export function BiometryVerifier({ sessionId, apiBase, onSuccess, onError }: Props) {
  const [loading, setLoading] = useState(false);
  const [tentativas, setTentativas] = useState(0);
  const [mode, setMode] = useState<'verify' | 'register' | null>(null);

  const isMountedRef = useRef(true);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  const executar = async () => {
    if (isMountedRef.current) setLoading(true);
    activeWebAuthnSessionId = sessionId;
    try {
      // 1. Buscar challenge (backend decide modo: verify ou register)
      const chalRes = await fetch(`${apiBase}/security/session/${sessionId}/biometry-challenge`);
      if (!chalRes.ok) throw new Error('Erro ao obter challenge');
      const chalData = await chalRes.json();

      // Biometria NUNCA é pulada — o backend sempre retorna mode: 'register' ou 'verify'

      if (chalData.mode === 'register') {
        // MODO REGISTRO: primeiro acesso — cadastrar passkey
        if (isMountedRef.current) setMode('register');
        const regResponse = await startRegistration(chalData.options.options);

        const verRes = await fetch(`${apiBase}/security/session/${sessionId}/biometry-register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ registrationResponse: regResponse }),
        });
        if (!verRes.ok) throw new Error('Cadastro biometrico nao confirmado');
        onSuccessRef.current();

      } else {
        // MODO VERIFICACAO: ja tem passkey — autenticar
        if (isMountedRef.current) setMode('verify');
        const { challenge, rpId, timeout } = chalData;

        const assertionResponse = await startAuthentication({
          optionsJSON: {
            challenge,
            rpId,
            timeout: timeout ?? 60000,
            userVerification: 'required',
            allowCredentials: [],
          }
        } as any);

        const verRes = await fetch(`${apiBase}/security/session/${sessionId}/biometry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assertionResponse }),
        });
        if (!verRes.ok) throw new Error('Biometria nao confirmada');
        onSuccessRef.current();
      }
    } catch (e: any) {
      if (isMountedRef.current) {
        const novas = tentativas + 1;
        setTentativas(novas);
        const details = e.name && e.message ? ` [${e.name}: ${e.message}]` : e.message ? ` [${e.message}]` : '';
        if (novas >= 3) {
          onErrorRef.current(`Máximo de tentativas atingido. Sessão bloqueada.${details}`);
        } else {
          const msg = e.name === 'NotAllowedError'
            ? `Biometria cancelada. Tentativa ${novas}/3.${details}`
            : `Falha na biometria. Tentativa ${novas}/3.${details}`;
          onErrorRef.current(msg);
        }
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
      activeWebAuthnSessionId = null;
    }
  };

  const isSecure = window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isSupported = typeof window.PublicKeyCredential !== 'undefined' && isSecure;

  useEffect(() => {
    // Auto-executa a biometria assim que o componente monta
    if (isSupported) {
      if (activeWebAuthnSessionId === sessionId) {
        console.log('[BiometryVerifier] WebAuthn request already active for session, skipping duplicate run.');
        return;
      }
      executar();
    }
  }, [sessionId, isSupported]);

  const isRegister = mode === 'register';

  if (!isSupported) {
    return (
      <div className="glass-card animate-fade-in-up" style={{ 
        textAlign: 'center', padding: '2.5rem 1.5rem', borderRadius: 24,
        position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', margin: '0 auto 1.5rem',
            background: 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(185,28,28,0.05))',
            border: '1px solid rgba(239,68,68,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 20px rgba(239,68,68,0.1)'
          }}>
            <Fingerprint size={36} strokeWidth={1.5} style={{ color: '#ef4444' }} />
          </div>

          <h2 style={{ 
            fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.75rem', 
            color: '#f8fafc', letterSpacing: '-0.02em' 
          }}>
            Biometria Indisponível
          </h2>
          
          <p style={{ fontSize: '0.88rem', color: '#94a3b8', marginBottom: '2rem', lineHeight: 1.6 }}>
            Este dispositivo ou navegador não suporta Passkey, ou a conexão não é segura.
            <br />
            <strong style={{ color: '#fca5a5' }}>O cadastro biométrico exige uma conexão HTTPS.</strong>
          </p>

          <div style={{
            padding: '1rem', borderRadius: 12, marginBottom: '2rem',
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            fontSize: '0.8rem', color: '#fca5a5', textAlign: 'left', lineHeight: 1.5
          }}>
            <strong>Por que isso acontece?</strong>
            <br />
            Para segurança dos seus dados, os navegadores bloqueiam o uso de biometria em endereços não criptografados (HTTP).
            <br /><br />
            <strong>Origem atual:</strong> {window.location.origin}
          </div>

          <button
            onClick={() => {
              // Limpar sessão e desconectar
              localStorage.removeItem('ac_token');
              localStorage.removeItem('ac_refresh_token');
              localStorage.removeItem('ac_user');
              localStorage.removeItem('bonding_session_id');
              sessionStorage.removeItem('bonding_session_id');
              window.location.href = '/login';
            }}
            style={{
              width: '100%', padding: '1.1rem 1rem', borderRadius: 16, border: 'none',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 4px 14px rgba(239, 68, 68, 0.35)',
              color: '#ffffff',
              fontSize: '1rem', fontWeight: 700,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}
          >
            Sair / Desconectar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card animate-fade-in-up" style={{ 
      textAlign: 'center', padding: '2.5rem 1.5rem', borderRadius: 24,
      position: 'relative', overflow: 'hidden'
    }}>
      {/* Glow background behind the card */}
      <div style={{
        position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
        width: 150, height: 150, background: 'rgba(234, 179, 8, 0.1)',
        filter: 'blur(40px)', zIndex: 0
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 20,
          textAlign: 'left',
          marginBottom: 24
        }}>
          {/* Coluna Esquerda: Ícone de digital */}
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(234,179,8,0.15), rgba(202,138,4,0.05))',
            border: '1px solid rgba(234,179,8,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 20px rgba(234,179,8,0.1)',
            flexShrink: 0
          }}>
            <Fingerprint size={36} strokeWidth={1.5} style={{ color: '#eab308' }} />
          </div>

          {/* Coluna Direita: Textos */}
          <div style={{ flexGrow: 1, minWidth: 0 }}>
            <h2 style={{ 
              fontSize: '1.25rem', fontWeight: 800, margin: 0, 
              color: '#f8fafc', letterSpacing: '-0.02em', lineHeight: 1.25
            }}>
              {isRegister ? 'Cadastre sua biometria' : 'Autorize com sua biometria'}
            </h2>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '6px 0 0', lineHeight: 1.5 }}>
              {isRegister
                ? 'Toque no sensor de impressao digital ou olhe para a camera para registrar sua identidade neste dispositivo.'
                : 'Toque no sensor de impressao digital ou olhe para a camera para confirmar sua identidade com seguranca maxima.'
              }
            </p>
          </div>
        </div>

        {tentativas > 0 && (
          <div style={{
            padding: '0.75rem', borderRadius: 10, marginBottom: '1.5rem',
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#fca5a5', fontSize: '0.85rem',
          }}>
            Tentativa {tentativas}/3 — tente novamente
          </div>
        )}

        <button
          onClick={executar}
          disabled={loading || tentativas >= 3}
          className={(loading || tentativas >= 3) ? "" : "premium-btn"}
          style={{
            width: '100%', padding: '1.1rem 1rem', borderRadius: 16, border: 'none',
            background: (loading || tentativas >= 3) ? 'rgba(255,255,255,0.05)' : undefined,
            color: (loading || tentativas >= 3) ? '#94a3b8' : undefined,
            fontSize: '1rem', fontWeight: 700,
            cursor: (loading || tentativas >= 3) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          }}
        >
          {loading
            ? 'Verificando...'
            : isRegister ? 'Cadastrar biometria' : 'Verificar identidade'
          }
        </button>
      </div>
    </div>
  );
}
